import { call, put, select, takeLatest } from 'redux-saga/effects'
import { performWithMutexHOG } from '../../../services/mutexUtility.service'
import { getServiceIdSelector, getUserSelector } from '../../user/selectors'
import { getPcrIdSelector } from '../selectors'
import { PcrDocsService } from '../../../services/pcrDocs.service'
import { IPcr } from '../../pcr/store/pcr.types'
import { ITripInformationState } from '../../tripInformation/store/tripInformation.types'
import { getTripInformationSelector } from '../../tripInformation/selectors'
import { getPickupAddressSelector } from '../../pickupAddress/selectors'
import { IPickupAddressState } from '../../pickupAddress/store/pickupAddress.types'
import { getCompleteChartSelector, getSignersSelector } from '../../completeChart/selectors'
import { ICompleteChartState, IServicesBilling, ISignerState } from '../../completeChart/store/completeChart.types'
import { getDestinationAddressSelector } from '../../destinationAddress/selectors/destinationAddress.selectors'
import { IDispositionState } from '../../destinationAddress/store/destinationAddress.types'
import { throwPcrException } from '../../pcr/store/pcr.actions'
import { ISituationState } from '../../ccHpi/store/ccHpi.types'
import { getSituationSelector } from '../../ccHpi/selectors'
import { getCrewListSelector } from '../../crew/selectors'
import { ICrewMemberList } from '../../crew/store/crew.types'
import { IUserState } from '../../user/store/user.types'
import { CompleteChartConstants } from '../../completeChart/store/completeChart.constants'
import { setCMSValue } from '../../completeChart/store/completeChart.actions'
import { getServicesBillingConfigSelector } from '../../configurations/selectors'
import { IServicesBillingConfig } from '../../configurations/store/configurations.types'
import { ISctMedicationOptions, ISctProcedureOptions } from '../../options/store/options.types'
import { getSctMedicationsOptionsSelector, getSctProceduresOptionsSelector } from '../../options/selectors'

function calculateEState(
  serv: IServicesBilling,
  refMode: number | undefined,
  recMode: number | undefined,
  schedId: number | undefined
) {
  let eStateVal = false

  if (serv.emergencyCriteria === 2) {
    if ([2, 3, 4].includes(refMode ?? -1) || [2, 3, 4].includes(recMode ?? -1)) {
      eStateVal = true
    } else {
      eStateVal = false
    }
  } else if (serv.emergencyCriteria === 3) {
    eStateVal = true
  } else if (serv.emergencyCriteria === 1) {
    eStateVal = true
    if (schedId === 1) {
      eStateVal = false
    }
  } else {
    eStateVal = false
  }

  return eStateVal
}

const extractMedicationProcedureField2Values = (pcrDoc: IPcr, serv: IServicesBilling): Array<number | undefined> => {
  const medRoutesQualifyingForALS = [2, 3, 7, 12]
  const extractedValues =
    pcrDoc.patient.medicationAdministeredStateList
      ?.map((item) => item.addMedicationAdministeredData)
      ?.flat()
      ?.filter((item) => item.medication?.id !== serv.als2MedsExclude)
      ?.map((item) => item.route?.id) ?? []

  return extractedValues.filter((v) => v && medRoutesQualifyingForALS.includes(v))
}

// ROUTE - IV - Push
const extractMedicationProcedureIVP = (pcrDoc: IPcr, sctIvpMedsOptions: ISctMedicationOptions) => {
  const medicationsWithRoute2 =
    pcrDoc.patient.medicationAdministeredStateList
      ?.map((item) => item.addMedicationAdministeredData)
      ?.flat()
      ?.filter((item) => item.route?.id === 2) ?? []

  return sctIvpMedsOptions
    .filter((option) => medicationsWithRoute2.some((med) => med.medication?.id === option.id))
    .map((option) => option.name) as string[]
}

// IV - Drip
const extractMedicationProcedureDrip = (pcrDoc: IPcr, sctDripMedsOptions: ISctMedicationOptions) => {
  const medicationsWithRoute12 =
    pcrDoc.patient.medicationAdministeredStateList
      ?.map((item) => item.addMedicationAdministeredData)
      ?.flat()
      ?.filter((item) => item.route?.id === 12) ?? []

  return sctDripMedsOptions
    .filter((option) => medicationsWithRoute12.some((med) => med.medication?.id === option.id))
    .map((option) => option.name) as string[]
}

const extractTitrateProcedure = (pcrDoc: IPcr, sctDripMedsOptions: ISctMedicationOptions, sctMeds: number) => {
  const titrateProcedures = pcrDoc.patient.titrateStateList

  return sctDripMedsOptions
    .filter((option) =>
      titrateProcedures?.some((titrate) => {
        if (titrate.medication?.id === option.id) {
          return true
        }
        // Only check name match if SCT_MEDS > 0 and no ID exists
        if (sctMeds > 0 && !titrate.medication?.id && titrate.medication?.name === option.name) {
          return true
        }
        return false
      })
    )
    .map((option) => option.name) as string[]
}

const extractChestTubeProcedures = (pcrDoc: IPcr, sctOptionsMap: Map<number, any>) => {
  const chestTubeDrains = pcrDoc.patient.drainStateList?.filter((drain) => drain.action?.name === 'Chest Tube') ?? []
  const chestTubeProcedures: string[] = []

  for (const drain of chestTubeDrains) {
    if (drain.side?.name === 'Right') {
      const option = sctOptionsMap.get(1)
      if (option?.name) {
        chestTubeProcedures.push(option.name)
      }
    } else if (drain.side?.name === 'Left') {
      const option = sctOptionsMap.get(2)
      if (option?.name) {
        chestTubeProcedures.push(option.name)
      }
    }
  }

  return chestTubeProcedures
}

const extractVentilatorProcedures = (pcrDoc: IPcr, sctOptionsMap: Map<number, any>) => {
  const ventilatorProcedures: string[] = []

  if (pcrDoc.patient.ventilatorStateList && pcrDoc.patient.ventilatorStateList.length > 0) {
    ventilatorProcedures.push('Ventilator')
  }

  const option5 = sctOptionsMap.get(5)

  // Pressure Control
  if (option5?.name) {
    const ventilators =
      pcrDoc.patient.ventilatorStateList?.filter((ventilator) => ventilator.breathType?.id === 2) ?? []
    if (ventilators.length > 0) {
      ventilatorProcedures.push(option5.name)
    }
  }

  const option6 = sctOptionsMap.get(6)
  if (option6?.name) {
    const ventilators = pcrDoc.patient.ventilatorStateList?.filter((ventilator) => ventilator.peep > 5) ?? []
    if (ventilators.length > 0) {
      ventilatorProcedures.push(option6.name)
    }
  }

  return ventilatorProcedures
}

const extractIntubationProcedures = (pcrDoc: IPcr, sctOptionsMap: Map<number, any>) => {
  const intubationProcedures: string[] = []
  let hasIntubationRecord = false

  const intubations = pcrDoc.patient.intubationStateList ?? []
  for (const intubation of intubations) {
    if (intubation.method?.name) {
      const matchingOption = Array.from(sctOptionsMap.values()).find((opt) =>
        opt.name?.includes(intubation.method?.name ?? '')
      )
      if (matchingOption?.name) {
        intubationProcedures.push(matchingOption.name)
        hasIntubationRecord = true
      }
    }
  }

  return { intubationProcedures, hasIntubationRecord }
}

const extractAirwayProcedures = (pcrDoc: IPcr, sctOptionsMap: Map<number, any>, hasIntubationRecord: boolean) => {
  const airwayProcedures: string[] = []

  const Tracheostomy = sctOptionsMap.get(17)
  // ID 3 is Tracheostomy
  if (Tracheostomy?.name && pcrDoc.prNabc?.airType?.id === 3) {
    airwayProcedures.push(Tracheostomy.name)
  }

  const Cricothyrotomy = sctOptionsMap.get(15)
  // ID 4 is Cricothyrotomy
  if (Cricothyrotomy?.name && !hasIntubationRecord && pcrDoc.prNabc?.airType?.id === 4) {
    airwayProcedures.push(Cricothyrotomy.name)
  }

  return airwayProcedures
}

const extractPacemakerProcedures = (pcrDoc: IPcr, sctOptionsMap: Map<number, any>) => {
  const pacemakerProcedures: string[] = []

  const externalPacing = sctOptionsMap.get(7)
  // ID 1 is Transcutaneous
  if (externalPacing?.name && pcrDoc.prPm?.[0]?.type?.id === 1) {
    pacemakerProcedures.push(externalPacing.name)
  }

  const internalPacing = sctOptionsMap.get(8)
  // ID 2 is Transvenous
  if (internalPacing?.name && pcrDoc.prPm?.[0]?.type?.id === 2) {
    pacemakerProcedures.push(internalPacing.name)
  }

  return pacemakerProcedures
}

const extractIABPProcedures = (pcrDoc: IPcr, sctOptionsMap: Map<number, any>) => {
  const iabpProcedures: string[] = []

  const iabpOption = sctOptionsMap.get(9)
  if (iabpOption?.name) {
    const iabpCriticalCare =
      pcrDoc.patient.criticalCareStateList?.filter((care) => care.action?.name?.includes('IABP')) ?? []
    if (iabpCriticalCare.length > 0) {
      iabpProcedures.push(iabpOption.name)
    }
  }

  return iabpProcedures
}

const extractArterialLineProcedures = (pcrDoc: IPcr, sctOptionsMap: Map<number, any>) => {
  const arterialLineProcedures: string[] = []

  const arterialLineOption = sctOptionsMap.get(3)
  if (arterialLineOption?.name) {
    if (pcrDoc.prNabc?.cvArt?.[0]?.id) {
      arterialLineProcedures.push(arterialLineOption.name)
    } else {
      const hasArterialLineVitals = pcrDoc.patient?.patientVitals?.some(
        (vital) => vital.bloodPressureMethod?.id === 3150
      )
      if (hasArterialLineVitals) {
        arterialLineProcedures.push(arterialLineOption.name)
      }
    }
  }

  return arterialLineProcedures
}

const extractSwanGanzProcedures = (pcrDoc: IPcr, sctOptionsMap: Map<number, any>) => {
  const swanGanzProcedures: string[] = []

  const swanGanzOption = sctOptionsMap.get(4)
  if (swanGanzOption?.name) {
    const hasSwanGanz = pcrDoc.prNabc?.cvSwan?.id && pcrDoc.prNabc.cvSwan.id > 0
    if (hasSwanGanz) {
      swanGanzProcedures.push(swanGanzOption.name)
    }
  }

  return swanGanzProcedures
}

const extractSpecialtyEquipmentProcedures = (pcrDoc: IPcr, sctOptionsMap: Map<number, any>) => {
  const specialtyEquipmentProcedures: string[] = []
  const equipment = pcrDoc.patient.tripInformation?.equipment ?? []

  const iabpOption = sctOptionsMap.get(10)
  if (iabpOption?.name && equipment.some((eq) => eq?.id === 1)) {
    specialtyEquipmentProcedures.push(iabpOption.name)
  }

  const leftVadOption = sctOptionsMap.get(11)
  if (leftVadOption?.name && equipment.some((eq) => eq?.id === 2)) {
    specialtyEquipmentProcedures.push(leftVadOption.name)
  }

  const rightVadOption = sctOptionsMap.get(12)
  if (rightVadOption?.name && equipment.some((eq) => eq?.id === 3)) {
    specialtyEquipmentProcedures.push(rightVadOption.name)
  }

  const ecmoOption = sctOptionsMap.get(13)
  if (ecmoOption?.name && equipment.some((eq) => eq?.id === 4)) {
    specialtyEquipmentProcedures.push(ecmoOption.name)
  }

  const isoletteOption = sctOptionsMap.get(18)
  if (isoletteOption?.name && equipment.some((eq) => eq?.id === 5)) {
    specialtyEquipmentProcedures.push(isoletteOption.name)
  }

  return specialtyEquipmentProcedures
}

const extractFluidsColloids = (pcrDoc: IPcr, sctOptionsMap: Map<number, any>) => {
  const fluidsColloidsProcedures: string[] = []

  const bloodProductOption = sctOptionsMap.get(14)
  if (bloodProductOption?.name) {
    const colloidsDuringTransport = Number(pcrDoc.fluids?.duringIntakeColl || 0)
    if (colloidsDuringTransport > 0) {
      fluidsColloidsProcedures.push(bloodProductOption.name)
    }
  }

  return fluidsColloidsProcedures
}

const extractProcedureValuesField3 = (pcrDoc: IPcr): Array<string> => {
  const extractedField3Values = {
    airwayActions: pcrDoc.patient.airwayStateList?.map((item) => item.action) ?? [],
    cardiacActions: pcrDoc.patient.cardiacStateList?.map((item) => item.action) ?? [],
    childbirthActions: pcrDoc.patient.childbirthOBStateList?.map((item) => item.action) ?? [],
    drainActions: pcrDoc.patient.drainStateList?.map((item) => item.action) ?? [],
    hospitalNotifyMethods: pcrDoc.patient.hospitalNotifyStateList?.map((item) => item.method) ?? [],
    ultrasoundFindings: pcrDoc.patient.imagingUltrasoundStateList?.map((item) => item.findings) ?? [],
    immobilizationDevicesUsed: pcrDoc.patient.immobilizationStateList?.map((item) => item.devicesUsed) ?? [],
    intubationMethods: pcrDoc.patient.intubationStateList?.map((item) => item.method) ?? [],
    initiateIvTypes: pcrDoc.patient.initiateIvStateList?.map((item) => item.iVType) ?? [],
    medicalConsultMethods: pcrDoc.patient.medicalConsultStateList?.map((item) => item.method) ?? [],
    operationsField3: pcrDoc.patient.operationsStateList?.map((item) => item.field3) ?? [],
    rescueTypes: pcrDoc.patient.rescueStateList?.map((item) => item.rescueTypes) ?? [],
    titrateRoutes: pcrDoc.patient.titrateStateList?.map((item) => item.route) ?? [],
    woundCareActions: pcrDoc.patient.woundCareStateList?.map((item) => item.action) ?? [],
    stretcherChairMovingTo: pcrDoc.patient.operationsStateList?.map((item) => item.stretcherChairMovingTo) ?? [],
    jointCommissionTimeOut: pcrDoc.patient.operationsStateList?.map((item) => item.jointCommissionTimeOut) ?? [],
    safetyBrief: pcrDoc.patient.operationsStateList?.map((item) => item.safetyBrief) ?? [],
    hearingPrecautions: pcrDoc.patient.operationsStateList?.map((item) => item.hearingPrecautions) ?? [],
    environmentalControls: pcrDoc.patient.operationsStateList?.map((item) => item.environmentalControls) ?? [],
    safetyRestraints: pcrDoc.patient.operationsStateList?.map((item) => item.safetyRestraints) ?? [],
    seizurePrecautions: pcrDoc.patient.operationsStateList?.map((item) => item.seizurePrecautions) ?? [],
    patientLoadedCold: pcrDoc.patient.operationsStateList?.map((item) => item.patientLoadedCold) ?? [],
    patientLoadedHot: pcrDoc.patient.operationsStateList?.map((item) => item.patientLoadedHot) ?? [],
    patientOffloadCold: pcrDoc.patient.operationsStateList?.map((item) => item.patientOffloadCold) ?? [],
    patientOffloadHot: pcrDoc.patient.operationsStateList?.map((item) => item.patientOffloadHot) ?? [],
    patientMonitoring: pcrDoc.patient.operationsStateList?.map((item) => item.patientMonitoring) ?? [],
    pediatricStretcherChair: pcrDoc.patient.operationsStateList?.map((item) => item.pediatricStretcherChair) ?? [],
    pediMate: pcrDoc.patient.operationsStateList?.map((item) => item.pediMate) ?? [],
    babyPod: pcrDoc.patient.operationsStateList?.map((item) => item.babyPod) ?? [],
    pediatricSafetyRestraintDevice:
      pcrDoc.patient.operationsStateList?.map((item) => item.pediatricSafetyRestraintDevice) ?? [],
    patientPPE: pcrDoc.patient.operationsStateList?.map((item) => item.patientPersonalProtectiveEquipment) ?? [],
  }
  return Object.values(extractedField3Values)
    .flat()
    .map((v) => v.id)
}

function* calculateCMS() {
  try {
    const serviceId: string = yield select(getServiceIdSelector)
    const pcrID: string = yield select(getPcrIdSelector)
    const pcrDocsService = new PcrDocsService(serviceId)
    const pcrDoc: IPcr = yield call([pcrDocsService, pcrDocsService.getPCR], pcrID)
    const completeChart: ICompleteChartState = yield select(getCompleteChartSelector)
    const pickupAddress: IPickupAddressState = yield select(getPickupAddressSelector)
    const situation: ISituationState = yield select(getSituationSelector)
    const crewList: ICrewMemberList = yield select(getCrewListSelector)
    const signerState: ISignerState = yield select(getSignersSelector)
    const { SCT_RN_IH, SCT_MEDS }: IServicesBillingConfig = yield select(getServicesBillingConfigSelector)

    const tripInformationState: ITripInformationState = yield select(getTripInformationSelector)
    const sctProcsOptionsList: ISctProcedureOptions = yield select(getSctProceduresOptionsSelector)
    const sctMedsOptionsList: ISctMedicationOptions = yield select(getSctMedicationsOptionsSelector)

    const sctIvpMedsOptions = sctMedsOptionsList.filter((med) => med.ivp)
    const sctDripMedsOptions: ISctMedicationOptions = sctMedsOptionsList.filter((med) => med.drip)

    const { unit, staffingLevel, dispatchPriority, scheduled, typeofService } = tripInformationState
    const { clinicalUserList } = signerState
    const { responseMode } = pickupAddress
    const { alsAssessment } = situation
    const destinationAddressState: IDispositionState = yield select(getDestinationAddressSelector)
    const { transportModeFromScene } = destinationAddressState
    const srv = completeChart.servicesBilling
    const als2Meds = extractMedicationProcedureField2Values(pcrDoc, srv)
    const listDetail = extractProcedureValuesField3(pcrDoc)
    const listProc = [...listDetail]

    const alsAllowedUsers = crewList.filter((crew) => {
      const userId = crew.userId?.id
      if (!userId) return false
      if (clinicalUserList?.length) {
        const matchedUser = clinicalUserList.find((user) => user?.id === userId)
        return matchedUser?.alsAllowed === 1
      }
      return false
    })
    const user: IUserState = yield select(getUserSelector)

    let cmsValue = ''
    let als2flag
    const eState = calculateEState(srv, responseMode?.id, transportModeFromScene?.id, scheduled?.id)

    const alsList1 = [
      '3-Lead EKG',
      '12-Lead EKG',
      'ALS Assessment',
      'Cardiac Monitor',
      'Cardioversion',
      'Central IV',
      'Cricothyrotomy',
      'Defibrillation',
      'External Pacing',
      'Foley',
      'Internal Pacing',
      'IO',
      'Nasotracheal',
      'Needle Thoracotomy',
      'Needle Thoracostomy',
      'NG Tube',
      'Peripheral IV',
      'Orotracheal',
      'OG Tube',
      'SIO',
    ]

    const alsList2 = [
      'Cardioversion',
      'Central IV',
      'Chest Tube',
      'Combitube',
      'Defibrillation',
      'External Pacing',
      'Internal Pacing',
      'IO',
      'Nasotracheal',
      'Needle Cricothyrotomy',
      'Needle Thoracotomy',
      'Needle Thoracostomy',
      'Orotracheal',
      'Surgical Cricothyroidotomy',
      'King Airway LTD',
      'EOA/EGTA',
      'Tracheostomy Tube',
      'CNS Intraventricular',
      'CNS Epidural',
      'Direct Laryngoscopy',
      'Video Laryngoscopy',
      'Combitube Maintenance',
      'Nasotracheal Maintenance',
      'Endotracheal Maintenance',
      'Pericardiocentesis',
    ]

    if (unit?.unitType === 5) {
      cmsValue = '1030'
    } else if (unit?.unitType === 6) {
      cmsValue = '1025'
    }

    if (!cmsValue) {
      als2flag = false

      if (staffingLevel?.isCCT === 1) {
        let sctOverride
        const sctDetails: string[] = []
        const isRN = clinicalUserList.some((user) => user?.isRN === 1)
        if (SCT_RN_IH === 1 && isRN) {
          sctOverride = true
        } else {
          sctOverride = false
        }

        if (typeofService?.isInterfacility === 1 || sctOverride) {
          if (
            (sctProcsOptionsList.length > 0 && sctIvpMedsOptions.length > 0 && sctDripMedsOptions.length > 0) ||
            sctOverride
          ) {
            const sctOptionsMap: any = new Map(
              sctProcsOptionsList.filter((opt) => opt.id !== undefined).map((opt) => [opt.id, opt])
            )

            if (sctOverride) {
              sctDetails.push('Advanced provider on-board')
            }

            const ivpMedicationNames = extractMedicationProcedureIVP(pcrDoc, sctIvpMedsOptions)
            sctDetails.push(...ivpMedicationNames)

            const dripMedicationNames = extractMedicationProcedureDrip(pcrDoc, sctDripMedsOptions)
            sctDetails.push(...dripMedicationNames)

            const titrateMedicationNames = extractTitrateProcedure(pcrDoc, sctDripMedsOptions, SCT_MEDS)
            sctDetails.push(...titrateMedicationNames)

            const fluidsColloids = extractFluidsColloids(pcrDoc, sctOptionsMap)
            sctDetails.push(...fluidsColloids)

            const chestTubeProcedures = extractChestTubeProcedures(pcrDoc, sctOptionsMap)
            sctDetails.push(...chestTubeProcedures)

            const ventilatorProcedures = extractVentilatorProcedures(pcrDoc, sctOptionsMap)
            sctDetails.push(...ventilatorProcedures)

            const { intubationProcedures, hasIntubationRecord } = extractIntubationProcedures(pcrDoc, sctOptionsMap)
            sctDetails.push(...intubationProcedures)

            const airwayProcedures = extractAirwayProcedures(pcrDoc, sctOptionsMap, hasIntubationRecord)
            sctDetails.push(...airwayProcedures)

            const pacemakerProcedures = extractPacemakerProcedures(pcrDoc, sctOptionsMap)
            sctDetails.push(...pacemakerProcedures)

            const iabpProcedures = extractIABPProcedures(pcrDoc, sctOptionsMap)
            sctDetails.push(...iabpProcedures)

            const arterialLineProcedures = extractArterialLineProcedures(pcrDoc, sctOptionsMap)
            sctDetails.push(...arterialLineProcedures)

            const swanGanzProcedures = extractSwanGanzProcedures(pcrDoc, sctOptionsMap)
            sctDetails.push(...swanGanzProcedures)

            const specialtyEquipmentProcedures = extractSpecialtyEquipmentProcedures(pcrDoc, sctOptionsMap)
            sctDetails.push(...specialtyEquipmentProcedures)
          }
          if (sctDetails.length) {
            cmsValue = '1020'
          }
        }
      }
    }

    if (!cmsValue) {
      let alsAssess = 0,
        alsType = 0

      if (pcrDoc?.patient?.patientVitals?.some((v) => v?.heartRateMethod)) {
        listDetail.push('Cardiac Monitor')
        listProc.push('Cardiac Monitor')
      }
      if (srv?.als1IVmeds) {
        alsList1.push('IV Medication')
      }
      if (!listProc.includes('IM Medication')) {
        alsList1.push('IM Medication')
      }
      if (!listProc.includes('IO') && als2Meds.includes(3)) {
        listDetail.push('IO')
      }
      if (als2Meds.includes(2) || als2Meds.includes(12)) {
        listProc.push('IV Medication')
      }
      if (pcrDoc?.prNabc?.airType?.id === 1) {
        listDetail.push('Endotracheal Maintenance')
        listProc.push('Endotracheal Maintenance')
      }
      if (pcrDoc?.prNabc?.airType?.id === 2) {
        listDetail.push('Nasotracheal Maintenance')
        listProc.push('Nasotracheal Maintenance')
      }
      if (pcrDoc?.prNabc?.airType?.id === 42) {
        listDetail.push('Combitube Maintenance')
        listProc.push('Combitube Maintenance')
      }

      if (dispatchPriority?.als || !dispatchPriority) {
        if (alsAssessment?.id && alsAssessment.id > 1) {
          alsAssess = 1
        }
      }
      if (!alsAssess && dispatchPriority?.als) {
        if (alsAllowedUsers.length > 0) {
          const assessedByIds = pcrDoc?.patient?.patientVitals
            ? pcrDoc?.patient?.patientVitals.map((vital) => vital.assessedBy?.id).filter((id) => id !== undefined)
            : []

          alsAssess = assessedByIds.findIndex((id) => Number(id) === Number(user.userId))
        }
      }

      if (staffingLevel?.isAls === 1 || staffingLevel?.isCCT === 1 || staffingLevel?.name === '') {
        alsType = 1
        if (als2Meds.length >= 3) {
          cmsValue = '1010'
          als2flag = true
        }

        if (!als2flag) {
          if (listDetail.some((item) => alsList2.includes(item))) {
            cmsValue = '1010'
            als2flag = true
          }
        }

        if (!als2flag) {
          if (listProc.some((item) => alsList1.includes(item))) {
            if (eState) {
              cmsValue = '1005'
            }
            cmsValue = '1000'
          }
          if (!cmsValue) {
            if (alsAssess) {
              listProc.push('ALS Assessment')

              if (eState) {
                cmsValue = '1005'
              } else {
                cmsValue = '990'
              }
            } else if (eState) {
              cmsValue = '995'
            } else {
              cmsValue = '990'
            }
          }
        }
      }
      if (staffingLevel?.isBls === 1 || (alsType === 1 && !cmsValue)) {
        if (listProc.includes('3-Lead EKG')) {
          if (eState) {
            cmsValue = '1005'
          } else {
            cmsValue = '1000'
          }
        }
        if (!cmsValue) {
          if (alsAssess) {
            listProc.push('ALS Assessment')
            if (eState) {
              cmsValue = '1005'
            } else {
              cmsValue = '1000'
            }
          } else if (eState) {
            cmsValue = '995'
          } else {
            cmsValue = '990'
          }
        }
      }

      if (!cmsValue && staffingLevel?.name !== '') {
        cmsValue = '-10'
      }
    }

    if (!cmsValue) {
      if (Number(staffingLevel?.id) === 0) {
        if (eState && dispatchPriority?.als === 0) {
          cmsValue = '995'
        } else if (eState && dispatchPriority?.als === 1) {
          cmsValue = '1005'
        } else if (!eState && dispatchPriority?.als === 0) {
          cmsValue = '990'
        } else if (!eState && dispatchPriority?.als === 1) {
          cmsValue = '990'
        }
      }
    }

    if (!cmsValue) {
      cmsValue = '-20'
    }

    if (pcrID) {
      try {
        yield put(setCMSValue(cmsValue))

        const pcrDocUpdated: IPcr = {
          ...pcrDoc,
          completeChart: {
            ...pcrDoc.completeChart,
            cmsSvcLevel: Number(cmsValue),
          },
        }
        yield call([pcrDocsService, pcrDocsService.savePCR], pcrDocUpdated)
      } catch (e) {
        yield put(throwPcrException(`calculateCMS() error: ${e}`))
      }
    } else {
      yield put(throwPcrException('pcrID not found'))
    }
  } catch (error) {
    yield put(throwPcrException(`calculateCMS() error: ${error}`))
  }
}

export function* calculateCMSSaga() {
  yield takeLatest([CompleteChartConstants.CALCULATE_BILLING_CMS], performWithMutexHOG, calculateCMS)
}
